import { InfoCircleFilled } from "@ant-design/icons";
import { getIntl } from "@umijs/max";
import { Button, Input, Modal, message } from "antd";
import { createStyles } from "antd-style";
import React, { useCallback, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";

const useStyles = createStyles(({ token }) => ({
  modalTitle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "16px",
    fontWeight: 500,
    color: token.colorText,
  },
  titleIcon: {
    color: token.colorPrimary,
    fontSize: "20px",
  },
  modalContent: {
    paddingTop: "16px",
    paddingBottom: "32px",
  },
  promptText: {
    color: token.colorTextSecondary,
    marginBottom: "16px",
    fontSize: "14px",
  },
  textArea: {
    borderRadius: "4px",
  },
}));

interface ReasonModalProps {
  type: "upgrade" | "downgrade";
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  visible?: boolean;
}

/**
 * 原因填写弹窗组件 (内部使用)
 */
const ReasonModalContent: React.FC<ReasonModalProps> = ({
  type,
  onConfirm,
  onCancel,
  visible = true,
}) => {
  const intl = getIntl();
  const { styles } = useStyles();
  const [reason, setReason] = useState("");

  const isUpgrade = type === "upgrade";

  const handleReasonChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setReason(e.target.value);
    },
    [],
  );

  const handleConfirm = useCallback(() => {
    if (!reason.trim()) {
      message.warning(
        intl.formatMessage({ id: "pages.common.reason.required" }),
      );
      return;
    }
    onConfirm(reason.trim());
  }, [reason, onConfirm, intl]);

  const modalTitle = useMemo(
    () => (
      <div className={styles.modalTitle}>
        <InfoCircleFilled className={styles.titleIcon} />
        <span>
          {intl.formatMessage({
            id: isUpgrade
              ? "pages.common.reason.upgrade"
              : "pages.common.reason.downgrade",
          })}
        </span>
      </div>
    ),
    [isUpgrade, intl, styles],
  );

  const promptText = useMemo(
    () =>
      intl.formatMessage({
        id: isUpgrade
          ? "pages.common.reason.upgrade.tip"
          : "pages.common.reason.downgrade.tip",
      }),
    [isUpgrade, intl],
  );

  const placeholder = useMemo(
    () => intl.formatMessage({ id: "pages.common.reason.placeholder" }),
    [intl],
  );

  const modalFooter = useMemo(
    () => [
      <Button key="cancel" onClick={onCancel}>
        {intl.formatMessage({ id: "pages.common.cancel" })}
      </Button>,
      <Button key="confirm" type="primary" onClick={handleConfirm}>
        {intl.formatMessage({ id: "pages.common.ok" })}
      </Button>,
    ],
    [onCancel, handleConfirm, intl],
  );

  return (
    <Modal
      open={visible}
      title={modalTitle}
      onCancel={onCancel}
      footer={modalFooter}
      width={440}
      centered
      destroyOnHidden
    >
      <div className={styles.modalContent}>
        <p className={styles.promptText}>{promptText}</p>
        <Input.TextArea
          className={styles.textArea}
          rows={4}
          placeholder={placeholder}
          value={reason}
          onChange={handleReasonChange}
          maxLength={200}
          showCount
        />
      </div>
    </Modal>
  );
};

/**
 * 弹出原因填写对话框
 * @param type 'upgrade' | 'downgrade'
 * @returns Promise<string>
 */
export const showReasonModal = (
  type: "upgrade" | "downgrade",
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const div = document.createElement("div");
    document.body.appendChild(div);
    const root = createRoot(div);

    const cleanup = () => {
      root.unmount();
      div.remove();
    };

    const onConfirm = (reason: string) => {
      cleanup();
      resolve(reason);
    };

    const onCancel = () => {
      cleanup();
      reject(new Error("CANCEL"));
    };

    root.render(
      <ReasonModalContent
        type={type}
        onConfirm={onConfirm}
        onCancel={onCancel}
        visible={true}
      />,
    );
  });
};
